import { shownTotal } from "./calc-total";
import type { PackageInfo } from "./types";

/**
 * The party and the money the activity page's side panel starts from.
 *
 * The panel used to start at its own default (1 adult, or the minimum party)
 * whatever the address said, so a guest who arrived with
 * `?date=…&adults=2&children=1` read 1,400 MAD in the panel and 2,100 MAD in
 * the booking sheet on the same page (Ouzoud day trip, traveller test
 * 2026-11-01). It now starts from the address, as the sheet does.
 */

export interface PartySelection {
  date?: string;
  time?: string;
  adults?: number;
  children?: number;
  infants?: number;
}

export interface SidebarParty {
  adults: number;
  children: number;
  infants: number;
  groups: number;
}

type Pricing = { adult: number; child: number; infant: number; extraGuest?: number | null };
type SidebarPackage = { maxGuests?: number | null; includedGuests?: number | null; pricing?: Pricing };

/** No package sells a child or an infant place (`-1` on every package). */
export function sellsAdultsOnly(packages: readonly SidebarPackage[] | null | undefined): boolean {
  const priced = (packages ?? []).filter((p) => p.pricing);
  if (priced.length === 0) return false;
  return priced.every((p) => p.pricing!.child < 0 && p.pricing!.infant < 0);
}

export function initialSidebarParty(o: {
  selection?: PartySelection | null;
  minParty: number;
  typePrice: 0 | 1 | 2 | 3;
  /** The lead package's pricing: a `-1` band is not sold, so it starts at 0. */
  pricing?: Pricing | null;
  /** Seats on one unit of a flat-rate package (a 2-seat buggy). */
  unitCap?: number | null;
}): SidebarParty {
  const sel = o.selection ?? null;
  const childSold = !o.pricing || o.pricing.child >= 0;
  const infantSold = !o.pricing || o.pricing.infant >= 0;
  const children = childSold ? Math.max(0, sel?.children ?? 0) : 0;
  const infants = infantSold ? Math.max(0, sel?.infants ?? 0) : 0;
  // The minimum party counts adults and children together (normalizeMinPeople).
  const adults = Math.max(sel?.adults ?? 0, o.minParty - children, 1);
  let groups = 1;
  if (o.typePrice === 3 && typeof o.unitCap === "number" && o.unitCap > 0) {
    // Enough machines to seat everyone, never more machines than drivers.
    groups = Math.max(1, Math.min(adults, Math.ceil((adults + children + infants) / o.unitCap)));
  }
  return { adults, children, infants, groups };
}

/**
 * The total the panel shows, in the guest's currency: every price converted
 * and rounded first, then added up, exactly as the booking sheet builds its
 * total (`shownTotal`). Converting the dirham total in one go gave 115 EUR in
 * the panel beside 2 x 57 = 114 EUR in the sheet (Fes medina tour).
 */
export function sidebarShownTotal(
  o: {
    typePrice: 0 | 1 | 2 | 3;
    /** The "from" price, used when the activity carries no package. */
    price: number;
    pkg?: SidebarPackage | null;
    guests: { adults: number; children: number; infants: number };
    nights: number;
    groups: number;
  },
  unit: (mad: number) => number,
): number {
  const pricing: Pricing = o.pkg?.pricing ?? { adult: o.price, child: -1, infant: -1 };
  const pkg: PackageInfo = {
    id: "",
    name: "",
    description: "",
    isMostPopular: false,
    maxGuests: o.pkg?.maxGuests ?? 0,
    includedGuests: o.pkg?.includedGuests ?? null,
    pricing: {
      adult: pricing.adult,
      child: pricing.child,
      infant: pricing.infant,
      transport: null,
      extraGuest: pricing.extraGuest ?? null,
    },
  };
  return shownTotal(
    {
      typePrice: o.typePrice,
      pkg,
      guests: o.guests,
      hours: 1,
      transport: false,
      nights: Math.max(1, o.nights),
      groups: Math.max(1, o.groups),
    },
    unit,
  );
}

const CARRIED_KEYS = ["date", "time", "adults", "children", "infants"] as const;

/**
 * The guest's day and party as a query string ("?date=…&adults=2"), for the
 * links that leave the activity page: breadcrumbs, "See all" and the
 * packages page. A plain href with no query drops them (CLAUDE.md, state
 * preservation). Malformed values are left behind rather than passed on.
 */
export function carriedPartyQuery(search: Record<string, unknown> | null | undefined): string {
  if (!search) return "";
  const params = new URLSearchParams();
  for (const key of CARRIED_KEYS) {
    const raw = search[key];
    if (raw === undefined || raw === null || raw === "") continue;
    const value = String(raw);
    if (key === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(value)) continue;
    if (key === "time" && !/^\d{2}:\d{2}$/.test(value)) continue;
    if (key !== "date" && key !== "time" && !/^\d{1,2}$/.test(value)) continue;
    params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** `href` with the carried query added, whether or not it already has one. */
export function withCarriedQuery(href: string, query: string): string {
  if (!query) return href;
  return href.includes("?") ? `${href}&${query.slice(1)}` : `${href}${query}`;
}
